/* =====================================================================
 * EmptyState — 공통 빈 상태 컴포넌트 (테마 기반)
 * ===================================================================== */

import React from 'react';
import styled, { keyframes } from 'styled-components';

interface EmptyStateProps {
  icon?: React.ReactNode;
  title?: string;
  titlePrefix?: string;
  titleSize?: string;
  rotatingTitles?: string[];
  rotatingIntervalMs?: number;
  rotatingTransitionMs?: number;
  description?: string;
  descriptionTone?: 'default' | 'strong';
  /**
   * 설명 문구를 한 줄로 강제(`white-space: nowrap`)하고 `max-width` 제한을
   * 해제한다. 기본값(false)에서는 기존처럼 320px 폭 안에서 자연스럽게
   * 줄바꿈된다. 에이전트 시작 화면처럼 한 줄 카피를 의도한 곳에서만 켠다.
   */
  descriptionNoWrap?: boolean;
  action?: React.ReactNode;
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  titlePrefix,
  titleSize,
  rotatingTitles,
  rotatingIntervalMs = 2200,
  rotatingTransitionMs = 520,
  description,
  descriptionTone = 'default',
  descriptionNoWrap = false,
  action,
  className,
}) => {
  const normalizedRotatingTitles = React.useMemo(
    () =>
      (rotatingTitles ?? [])
        .map((item) => item.trim())
        .filter((item) => item.length > 0),
    [rotatingTitles],
  );
  const hasRotatingTitle = normalizedRotatingTitles.length > 0;
  const maxRotatingTitleLength = React.useMemo(
    () => normalizedRotatingTitles.reduce((max, text) => Math.max(max, text.length), 0),
    [normalizedRotatingTitles],
  );
  const intervalMs = Math.max(1200, rotatingIntervalMs);
  const transitionMs = Math.max(200, rotatingTransitionMs);

  const [activeIndex, setActiveIndex] = React.useState(0);
  const [incomingIndex, setIncomingIndex] = React.useState<number | null>(null);
  const [isAnimating, setIsAnimating] = React.useState(false);

  React.useEffect(() => {
    setActiveIndex(0);
    setIncomingIndex(null);
    setIsAnimating(false);
  }, [normalizedRotatingTitles]);

  React.useEffect(() => {
    if (normalizedRotatingTitles.length <= 1) return undefined;
    let rafId: number | null = null;

    const intervalId = window.setInterval(() => {
      if (isAnimating) return;
      
      // 1. 다음 아이템을 준비 (아직 애니메이션 시작 안 함, 아래에 위치)
      const next = (activeIndex + 1) % normalizedRotatingTitles.length;
      setIncomingIndex(next);
      
      // 2. React 렌더링 사이클을 기다린 후 애니메이션 클래스 트리거
      rafId = window.requestAnimationFrame(() => {
        rafId = window.requestAnimationFrame(() => {
          setIsAnimating(true);
        });
      });
    }, intervalMs);

    return () => {
      window.clearInterval(intervalId);
      if (rafId !== null) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [activeIndex, isAnimating, intervalMs, normalizedRotatingTitles.length]);

  React.useEffect(() => {
    if (!isAnimating || incomingIndex === null) return undefined;

    const timeoutId = window.setTimeout(() => {
      // 3. 애니메이션 완료 후 현재 아이템을 교체하고 상태 초기화
      setActiveIndex(incomingIndex);
      setIsAnimating(false);
      setIncomingIndex(null);
    }, transitionMs);

    return () => window.clearTimeout(timeoutId);
  }, [incomingIndex, isAnimating, transitionMs]);

  const resolvedTitle = hasRotatingTitle ? (
    <TitleFlexWrapper>
      {titlePrefix && <TitlePrefix>{titlePrefix}</TitlePrefix>}
      <RotatingTitleViewport $minWidthCh={Math.max(maxRotatingTitleLength + 0.5, 3)}>
        <RotatingTitleItem 
          key={`current-${activeIndex}`} 
          $state="current" 
          $animating={isAnimating} 
          $transitionMs={transitionMs}
        >
          {normalizedRotatingTitles[activeIndex]}
        </RotatingTitleItem>
        {incomingIndex !== null && normalizedRotatingTitles[incomingIndex] && (
          <RotatingTitleItem 
            key={`next-${incomingIndex}`} 
            $state="next" 
            $animating={isAnimating} 
            $transitionMs={transitionMs}
          >
            {normalizedRotatingTitles[incomingIndex]}
          </RotatingTitleItem>
        )}
      </RotatingTitleViewport>
    </TitleFlexWrapper>
  ) : (
    title
  );

  return (
    <Container className={className}>
      {icon && <IconWrap>{icon}</IconWrap>}
      {(title || hasRotatingTitle) && <Title $fontSize={titleSize}>{resolvedTitle}</Title>}
      {description && (
        <Description $tone={descriptionTone} $noWrap={descriptionNoWrap}>
          {description}
        </Description>
      )}
      {action && <ActionWrap>{action}</ActionWrap>}
    </Container>
  );
};

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
  padding: 20px 20px 15%;
  animation: ${fadeIn} 0.4s ease;
`;

const IconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.textMuted};
  margin-bottom: 4px;
`;

const Title = styled.div<{ $fontSize?: string }>`
  font-size: ${({ $fontSize }) => $fontSize ?? '1rem'};
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: center;
  display: flex;
  justify-content: center;
`;

const TitleFlexWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0;
  /* Askyour(7글자)와 뒤의 단어 길이를 고려해 전체를 우측으로 더 이동시킴 */
  transform: translateX(0.8ch);
`;

const TitlePrefix = styled.span`
  font-weight: 700;
  color: ${({ theme }) => theme.colors.textPrimary};
  text-align: right;
  display: inline-block;
`;

const RotatingTitleViewport = styled.span<{ $minWidthCh: number }>`
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  position: relative;
  /* 뒤에 붙는 단어도 접두어(Askyour)와 같은 볼드체. 폭을 ch 단위로 재므로 여기서
   * 굵기를 맞춰 두어야 항목과 같은 글리프 폭으로 계산된다. */
  font-weight: 700;
  /* Edge/Windows font metrics need extra room for the final glyph. */
  width: ${({ $minWidthCh }) => `calc(${$minWidthCh}ch + 0.75ch)`};
  max-width: min(90vw, 100%);
  height: 1.25em;
  overflow: hidden;
  flex-shrink: 0;
  text-align: left;
`;

const RotatingTitleItem = styled.span<{
  $state: 'current' | 'next';
  $animating: boolean;
  $transitionMs: number;
}>`
  position: absolute;
  left: 0;
  right: 0;
  white-space: nowrap;
  text-align: left;
  line-height: 1.25;
  font-weight: inherit;
  color: ${({ theme }) => theme.colors.textPrimary};
  will-change: transform, opacity, filter;
  transition: ${({ $state, $animating, $transitionMs }) =>
    $state === 'next' && !$animating
      ? 'none'
      : `transform ${$transitionMs}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${$transitionMs}ms cubic-bezier(0.22, 1, 0.36, 1), filter ${$transitionMs}ms cubic-bezier(0.22, 1, 0.36, 1)`};

  ${({ $state, $animating }) =>
    $state === 'current'
      ? $animating
        ? `
      transform: translateY(-30%);
      opacity: 0;
      filter: blur(2px);
    `
        : `
      transform: translateY(0%);
      opacity: 1;
      filter: blur(0);
    `
      : $animating
        ? `
      transform: translateY(0%);
      opacity: 1;
      filter: blur(0);
    `
        : `
      transform: translateY(30%);
      opacity: 0;
      filter: blur(2px);
    `}

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: translateY(0);
    filter: none;
    opacity: ${({ $state, $animating }) =>
      $state === 'current' ? ($animating ? 0 : 1) : $animating ? 1 : 0};
  }
`;

const Description = styled.div<{ $tone: 'default' | 'strong'; $noWrap?: boolean }>`
  font-size: 0.8125rem;
  color: ${({ theme, $tone }) =>
    $tone === 'strong'
      ? (theme.mode === 'dark' ? 'rgba(255,255,255,0.94)' : 'rgba(0,0,0,0.88)')
      : theme.colors.textMuted};
  text-align: center;
  line-height: 1.5;
  max-width: ${({ $noWrap }) => ($noWrap ? 'none' : '320px')};
  ${({ $noWrap }) => ($noWrap ? 'white-space: nowrap;' : '')}

  /* 좁은 화면(모바일)에서는 한 줄 강제를 풀어, 가로 오버플로로 잘리는 대신
     기본 폭(320px) 안에서 자연스럽게 줄바꿈되도록 한다. */
  ${({ $noWrap, theme }) =>
    $noWrap
      ? `@media (max-width: ${theme.breakpoints.mobile}) {
           white-space: normal;
           max-width: 320px;
         }`
      : ''}
`;

const ActionWrap = styled.div`
  margin-top: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
`;
